<div class="timeline-activity-section">
  <div class="card-header">
    <h3 class="section-title">
      <mat-icon>timeline</mat-icon>
      {{moduleName}} Timeline & Activity
    </h3>
    <div class="section-actions">
      <button mat-icon-button matTooltip="Configure" [matMenuTriggerFor]="activityMenu">
        <mat-icon>settings</mat-icon>
      </button>
      <mat-menu #activityMenu="matMenu">
        <button mat-menu-item (click)="setActivityFilter('tasks')">
          <mat-icon [style.visibility]="activityFilter === 'tasks' ? 'visible' : 'hidden'">check</mat-icon>
          <span>Task Only</span>
        </button>
        <button mat-menu-item (click)="setActivityFilter('milestones')">
          <mat-icon [style.visibility]="activityFilter === 'milestones' ? 'visible' : 'hidden'">check</mat-icon>
          <span>Milestones Only</span>
        </button>
        <button mat-menu-item (click)="setActivityFilter('all')">
          <mat-icon [style.visibility]="activityFilter === 'all' ? 'visible' : 'hidden'">check</mat-icon>
          <span>Show All</span>
        </button>
      </mat-menu>
    </div>
  </div>
  <div class="timeline-content">
    <div class="timeline-column">
      <div class="timeline-column-head">
        <h4 class="column-title">
          <mat-icon>event</mat-icon>
          Timeline
        </h4>
        @if (canAddMilestone) {
          <div class="add-milestone-group">
            <button type="button" class="add-milestone-btn" (click)="openAddMilestone()" title="Add a milestone">
              <mat-icon>add</mat-icon>
              <span>Add Milestone</span>
            </button>
            <button type="button" class="add-milestone-caret" [matMenuTriggerFor]="milestoneAddMenu"
              title="More milestone options" aria-label="More milestone options">
              <mat-icon>keyboard_arrow_down</mat-icon>
            </button>
            <mat-menu #milestoneAddMenu="matMenu" class="milestone-add-menu"
              xPosition="before" yPosition="below" [overlapTrigger]="false">
              <button mat-menu-item type="button" class="milestone-add-menu__item" (click)="openAttachMilestone()">
                <mat-icon>link</mat-icon>
                <span>Attach Milestone</span>
              </button>
            </mat-menu>
          </div>
        }
      </div>

      <!-- Loading State -->
      @if (timelineLoading) {
        <div class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading timeline...</p>
        </div>
      }

      <!-- Timeline List -->
      @if (!timelineLoading && timelinePhases.length > 0) {
        <div class="timeline-list">
          @for (phase of timelinePhases; track phase) {
            <div class="timeline-item">
              <div class="timeline-marker" [class.completed]="phase.status === 'completed'"
                [class.in-progress]="phase.status === 'in_progress'"
                [class.pending]="phase.status === 'pending'"
                [class.clickable]="phase.status === 'pending' && canChangeMilestoneStatus"
                [attr.role]="phase.status === 'pending' && canChangeMilestoneStatus ? 'button' : null"
                [attr.title]="phase.status === 'pending' && canChangeMilestoneStatus ? 'Mark this milestone as in progress' : null"
                (click)="onMilestoneMarkerClick(phase, $event)">
                @if (phase.status === 'completed') {
                  <mat-icon>check_circle</mat-icon>
                }
                @if (phase.status === 'in_progress') {
                  <mat-icon>schedule</mat-icon>
                }
                @if (phase.status === 'pending') {
                  <mat-icon>radio_button_unchecked</mat-icon>
                }
              </div>
              <div class="timeline-content">
                <div class="timeline-header">
                  <p class="timeline-title">{{ phase.name }}</p>
                  <div class="timeline-header-right">
                    <p class="timeline-date">{{ phase.date }}</p>
                    <button type="button" class="add-task-btn"
                      (click)="openAddTaskForMilestone(phase, $event)"
                      title="Add task to this milestone" aria-label="Add task to this milestone">
                      <mat-icon>add</mat-icon>
                    </button>
                  </div>
                </div>
                <span class="timeline-status" [class]="phase.status">{{ phase.status.replace('_', ' ') | titlecase }}</span>

                <!-- Tasks attached to this milestone -->
                @if (phase.tasks?.length) {
                  <div class="timeline-tasks">
                    @for (task of phase.tasks; track task.id) {
                      <div class="timeline-task" [class]="task.status">
                        @if (completedTaskStatusId) {
                          <button type="button" class="task-complete-btn"
                            [class.done]="task.status === 'completed'"
                            [disabled]="task.status === 'completed' || taskCompleting[task.id]"
                            (click)="completeTask(task, phase, $event)"
                            [title]="task.status === 'completed' ? 'Completed' : 'Mark complete'">
                            <mat-icon>check</mat-icon>
                          </button>
                        } @else {
                          <span class="task-dot"></span>
                        }
                        <span class="task-name" [title]="task.name">{{ task.name }}</span>
                        @if (task.date) {
                          <span class="task-date">{{ task.date }}</span>
                        }
                      </div>
                    }
                  </div>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- Empty State -->
      @if (!timelineLoading && timelinePhases.length === 0) {
        <div class="empty-state">
          <mat-icon>info</mat-icon>
          <p>No milestones found</p>
        </div>
      }
    </div>
    <div class="activity-column">
      <h4 class="column-title">
        <mat-icon>history</mat-icon>
        Recent Activity
      </h4>
      @if (!activityLoading && recentActivities.length > 0) {
        <div class="activity-list" [style.height]="activityListsectionHeight">
          @for (activity of recentActivities; track activity) {
            <div class="activity-item">
              <div class="activity-avatar">
                <mat-icon>{{ activity.icon }}</mat-icon>
              </div>
              <div class="activity-content">
                <p class="activity-title">{{ activity.title }}</p>
                <p class="activity-meta">{{ activity.time }} @if (activity.user) {
                  <span>by {{ activity.user }}</span>
                }</p>
                @if (activity.description) {
                  <p class="activity-description" [innerHTML]="activity.description"></p>
                }
              </div>
            </div>
          }
        </div>
      }

      <!-- Load More Button -->
      @if (showLoadMoreActivities && !activityLoading) {
        <div class="load-more-container">
          <button mat-button class="load-more-btn" (click)="loadMoreActivities()">
            <mat-icon>expand_more</mat-icon>
            Load More Activities
          </button>
        </div>
      }

      @if (activityLoading && recentActivities.length === 0) {
        <div class="loading-state">
          <mat-spinner diameter="40"></mat-spinner>
          <p>Loading activities...</p>
        </div>
      }
      @if (activityLoading && recentActivities.length > 0) {
        <div class="loading-state-inline">
          <mat-spinner diameter="30"></mat-spinner>
        </div>
      }
      @if (!activityLoading && recentActivities.length === 0) {
        <div class="empty-state">
          <mat-icon>info</mat-icon>
          <p>No recent activities found</p>
        </div>
      }
    </div>
  </div>
</div>
